import React from 'react';
import { useNavigate } from 'react-router-dom';
import HeroSection from '@/components/home/HeroSection';
import CompactTrustStats from '@/components/home/CompactTrustStats';
import SocialProofBar from '@/components/conversion/SocialProofBar';
import ConversionOptimizedFeatures from '@/components/home/ConversionOptimizedFeatures';
import StrategicInternalLinking from '@/components/home/StrategicInternalLinking';
import HowItWorksSection from '@/components/home/HowItWorksSection';
import TestimonialsSection from '@/components/home/TestimonialsSection';
import CtaSection from '@/components/home/CtaSection';
import AvailableOnlineSection from '@/components/home/AvailableOnlineSection';
import FeaturedTherapistsSection from '@/components/home/FeaturedTherapistsSection';
import MasterSEOController from '@/components/seo/MasterSEOController';
import ValueProposition from '@/components/conversion/ValueProposition';
import PatientConversionCTA from '@/components/conversion/PatientConversionCTA';
import { RealTimeSocialProof } from '@/components/conversion/RealTimeSocialProof';
import { ExitIntentPopup } from '@/components/conversion/ExitIntentPopup';
import { MobileStickyCTA } from '@/components/conversion/MobileStickyCTA';
import { LiveChatWidget } from '@/components/conversion/LiveChatWidget';
import { PatientROICalculator } from '@/components/conversion/PatientROICalculator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useLanguage } from '@/context/LanguageContext';

const Index = () => {
  const { t } = useLanguage();
  
  const faqs = [
    {
      question: "How do I find the right therapist for me?",
      answer: "Use our advanced search filters to find therapists by specialty, language, location, and availability. You can also try our AI matching tool that recommends therapists based on your specific needs and preferences."
    },
    {
      question: "Are all therapists verified and licensed?",
      answer: "Yes, all therapists on our platform are verified professionals who have completed the Master in Brief Psychotherapy by Dr. José Luis Marín and hold valid licenses in their respective countries."
    },
    {
      question: "What are the session prices?",
      answer: "Session prices vary by region and therapist, ranging from $25 in some countries to $200 in others. This allows you to find affordable quality care that fits your budget."
    },
    {
      question: "Can I have sessions online or in-person?",
      answer: "Yes! We offer both online and in-person sessions. Many therapists offer flexible scheduling with availability 24/7 for online sessions."
    },
    {
      question: "How do I become a therapist on the platform?",
      answer: "To join as a therapist, you must have completed the Master in Brief Psychotherapy taught by Dr. José Luis Marín. Apply through our 'For Therapists' page and complete the verification process."
    },
    {
      question: "Is my information secure and confidential?",
      answer: "Absolutely. We use bank-level encryption and comply with international privacy standards. All sessions and personal data are 100% confidential and secure."
    }
  ];

  return (
    <>
      {/* New Hero with Search First */}
      <HeroSection />
      
      {/* Conversion Optimization Components */}
      <RealTimeSocialProof />
      <ExitIntentPopup />
      <MobileStickyCTA variant="find" />
      
      {/* Compact Trust Stats Bar */}
      <CompactTrustStats />
      
      {/* Social Proof */}
      <SocialProofBar />
      
      {/* How It Works - Simplified to 3 steps */}
      <HowItWorksSection />
      
      {/* Available Now Therapists */}
      <AvailableOnlineSection />
      
      {/* Testimonials with Video */}
      <TestimonialsSection />
      
      {/* Why Choose Us */}
      <ValueProposition />
      <ConversionOptimizedFeatures />
      
      {/* Featured Therapists */}
      <FeaturedTherapistsSection />
      
      {/* ROI Calculator - Patient Only */}
      <PatientROICalculator />
      
      {/* FAQ Section - Top 6 Questions */}
      <section className="container py-16 my-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      
      {/* Final CTA */}
      <CtaSection />
      
      {/* Patient Conversion CTA */}
      <PatientConversionCTA />
      
      {/* Live Chat Widget */}
      <LiveChatWidget />
    </>
  );
};

export default Index;
